import {
  MapPinIcon,
  ClockIcon,
  PencilSimpleIcon,
  BellIcon,
  BellSlashIcon,
} from '@phosphor-icons/react';
import type { EventHeaderProps } from '@/lib/types/events.types';
import {
  formatEventDate,
  formatEventTimeRange,
  formatRsvpDeadline,
} from '@/lib/utils/events.utils';
import { getInvitationGroupChips } from '@/lib/utils/invitation-groups.utils';
import { EventDetailsContent } from '@/content/EventDetailsContent';
import { EventSeriesBanner } from './EventSeriesBanner';

export function EventHeader({
  event,
  isAdmin,
  onEditClick,
  hasNotificationsEnabled,
  seriesBanner,
}: EventHeaderProps) {
  const invitationGroupChips = getInvitationGroupChips(event.invitation_groups);

  return (
    <div
      className="px-4 pt-4 pb-6 relative flex flex-col rounded-lg mt-4"
      style={{
        backgroundImage: 'url(/assets/images/event-header-bg.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        minHeight: '55vh',
      }}
    >
      <div className="relative z-10 flex flex-col flex-1 justify-between">
        <div>
          <div className="flex items-start justify-between mb-4">
            <img
              src="/assets/images/rise-logo-white-solid.png"
              alt="Rise Logo"
              className="h-12 w-auto"
            />
            <div className="flex items-center gap-3">
              {isAdmin && (
                <button
                  onClick={onEditClick}
                  data-testid="event-edit-button"
                  className="text-slate-50 hover:text-purple-300 transition-colors"
                  aria-label={EventDetailsContent.editButton.ariaLabel}
                >
                  <PencilSimpleIcon size={26} weight="regular" />
                </button>
              )}
              {hasNotificationsEnabled ? (
                <BellIcon size={26} weight="fill" />
              ) : (
                <BellSlashIcon size={26} weight="regular" />
              )}
            </div>
          </div>

          <h1
            data-testid="event-title"
            className="wrap-break-word text-[clamp(32px,10vw,48px)]"
          >
            {event.title}
          </h1>
          <p className="text-lead">{formatEventDate(event.start_time)}</p>
        </div>

        {/* Bottom section: Location, Time, RSVP Deadline */}
        <div>
          {event.location && (
            <div className="flex items-start gap-2">
              <MapPinIcon size={24} weight="regular" className="flex-shrink-0" />
              <span className="min-w-0 break-words [overflow-wrap:anywhere] text-lead">
                {event.location}
              </span>
            </div>
          )}

          <div className="flex items-center gap-2">
            <ClockIcon size={24} weight="regular" />
            <span className="text-lead">
              {formatEventTimeRange(event.start_time, event.end_time)}
            </span>
          </div>

          {event.rsvp_deadline && (
            <p className="text-small text-purple-300 mt-2">
              {formatRsvpDeadline(event.rsvp_deadline)}
            </p>
          )}

          {isAdmin && invitationGroupChips.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {invitationGroupChips.map((chip) => (
                <span
                  key={chip.role}
                  className={`px-3 py-1 text-small font-medium rounded-[6px] border ${chip.className}`}
                >
                  {chip.label}
                </span>
              ))}
            </div>
          )}

          {seriesBanner && (
            <EventSeriesBanner
              seriesId={event.series_id!}
              currentEventId={event.id}
              currentPosition={seriesBanner.currentPosition}
              totalEvents={seriesBanner.totalEvents}
              onClick={seriesBanner.onBannerClick}
            />
          )}
        </div>
      </div>
    </div>
  );
}
